import { TFunction } from "i18next";
import { AttachmentDownloadResult } from "./hooks";

// Names a report can be given that a filename cannot carry. Separators and the
// characters Windows refuses would either break the path or have the browser
// silently substitute its own name for the whole thing; control characters
// truncate the name at the byte that terminates a header; and the bidi
// overrides reverse how the rest of the name is drawn, so a stem ending in one
// can display an extension the file does not have.
const CHARACTERS_A_FILENAME_CANNOT_CARRY =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u001f\u007f-\u009f\u200e\u200f\u202a-\u202e\u2066-\u2069\\/:*?"<>|]/g;

// Windows drops a trailing dot or space silently, which turns "Q3." into a
// name the user did not choose — and "Q3." + ".xlsx" into "Q3..xlsx" first.
const TRAILING_DOTS_AND_SPACES = /[. ]+$/;

// Every substitution above leaves a space behind, so a stem that carried two
// of them in a row would be saved with a gap the user never typed.
const RUNS_OF_WHITESPACE = /\s+/g;

// Long enough for any name the rename field accepts, short enough that the
// extension still survives the filesystem's own limit on a path component.
const LONGEST_USABLE_STEM = 200;

// Swaps the name a file is saved under while keeping the extension the server
// gave it. The stored filename is written once, when the attachment is created,
// so a report renamed afterwards still downloads under its original name — but
// only the name is stale, never the format, and the extension is what tells the
// operating system how to open the file.
export const renameKeepingExtension = (filename: string, stem: string) => {
  const lastDot = filename.lastIndexOf(".");
  const extension = lastDot > 0 ? filename.slice(lastDot) : "";
  const safeStem = stem
    .replace(CHARACTERS_A_FILENAME_CANNOT_CARRY, " ")
    .replace(RUNS_OF_WHITESPACE, " ")
    .slice(0, LONGEST_USABLE_STEM)
    .trim()
    .replace(TRAILING_DOTS_AND_SPACES, "");
  return safeStem ? `${safeStem}${extension}` : filename;
};

export const downloadBlob = (attachmentData: AttachmentDownloadResult) => {
  const blobUrl = URL.createObjectURL(attachmentData.blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", attachmentData.filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
};

// Byte ceilings are stated to the user in whole binary units ("100 MB"), the
// same way the design writes them.
export const formatBytes = (bytes: number) => {
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  // Whole numbers stay whole; anything else keeps a single decimal so a
  // 1.5 MB ceiling does not read as "2 MB". The decimal truncates rather than
  // rounds: these numbers are stated as limits, and a rounded-up limit tells
  // the user a file is allowed that the field will reject.
  const truncated = Number.isInteger(value)
    ? value
    : Math.floor(value * 10) / 10;
  return `${truncated} ${units[unitIndex]}`;
};

// Turns an `accept` list into the format names the helper text shows,
// deduplicated in the order given. `accept` takes both extensions and MIME
// types and the field enforces both, so both are named: an extension loses its
// dot, a MIME type is reduced to the part that identifies it ("application/pdf"
// reads as PDF, "image/*" as IMAGE).
export const formatAcceptedFormats = (accept: string) => {
  const seen = new Set<string>();
  return accept
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
    .map((entry) => {
      if (entry.startsWith(".")) return entry.slice(1).toUpperCase();
      if (!entry.includes("/")) return "";
      const [type, subtype] = entry.split("/");
      return (subtype === "*" ? type : subtype).toUpperCase();
    })
    .filter((format) => {
      if (!format || seen.has(format)) return false;
      seen.add(format);
      return true;
    })
    .join(", ");
};

export const contentTypeMap: { [key: string]: string } = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  svg: "image/svg+xml",
  txt: "text/plain",
  html: "text/html",
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
};

interface ErrorMessageByStatusCode {
  [statusCode: number]: string;
}

// Callers that already resolved a message (an unsupported file type rejected
// before upload) pass the text itself; upload failures pass a status code.
// An unmapped code still has to say something — a red row with no message
// leaves the user with no idea what went wrong.
export const getErrorByCode = (
  t: TFunction<"translation", undefined>,
  error: number | string
) => {
  if (typeof error === "string") return error;

  const errorByCode: ErrorMessageByStatusCode = {
    404: t("attachments.errors.somethingWentWrong"),
    500: t("attachments.errors.somethingWentWrong"),
    400: t("attachments.errors.fileNotSupported")
  };
  return errorByCode[error] ?? t("attachments.errors.somethingWentWrong");
};
